import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { EmeraldModalModule } from '@emerald-angular/modal';
import { EmeraldButtonModule } from '@emerald-angular/button';
import { EmeraldAccordionGroupModule } from '@emerald-angular/accordion-group';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonIcon, ButtonLabel, ButtonSelection, PersonaType, Status, StatusClass } from '@constants/global.enum';
import { GlobalErrorService } from '@services/global-error.service';
import { CandidateFeedbackService } from './candidate-feedback.service';
import {
  FeedbackResponse,
  Candidate,
  Feedback,
  UpdateFeedbackRequest,
} from './candidate-feedback.model';
import { EmeraldIconModule } from '@emerald-angular/icon';
import { EmeraldDatepickerModule } from '@emerald-angular/datepicker';
import { EmeraldInputModule } from '@emerald-angular/input';
import { EmeraldDropdownModule } from '@emerald-angular/dropdown';
import { offsetMinutes } from '@shared/config/allowedKeys.config';
import { formatSingleDate, formatTimeTo12Hour } from '@utils/utils';
import { yearDateFormat } from '@constants/dateFormat';

@Component({
  selector: 'app-candidate-feedback',
  standalone: true,
  imports: [
    CommonModule,
    EmeraldButtonModule,
    EmeraldModalModule,
    EmeraldAccordionGroupModule,
    ReactiveFormsModule,
    EmeraldIconModule,
    EmeraldDatepickerModule,
    EmeraldInputModule,
    EmeraldDropdownModule,
  ],
  templateUrl: './candidate-feedback.component.html',
  styleUrl: './candidate-feedback.component.scss',
})
export class CandidateFeedbackComponent implements OnInit {
  @Input() fieldsVisibility: string = '';
  @Input() candidateId: string = '';
  @Output() sendFeedbackStatus = new EventEmitter<string>();
  @ViewChild('timeline') timelineRef?: ElementRef<HTMLDivElement>;

  formatTimeTo12Hour = formatTimeTo12Hour;
  candidateData: Candidate | null = null;
  candidateName: string = '';
  candidateStatus: string = '';
  candidateExp: number = 0;
  vendorName: string = '';
  feedbackComments: {
    title: string;
    content: string;
    status: string;
    updatedBy: string;
    updatedDate: string;
    interviewDate?: string;
    interviewTime?: string;
    interviewMode?: string;
    open: boolean;
  }[] = [];
  personaType = PersonaType;
  buttonLabel: string = '';
  buttonIcon: string = '';
  status = Status;
  buttonSelection = ButtonSelection
  opened: boolean = false;
  openSingle: boolean = true;
  feedbackForm: FormGroup;
  feedbackStatus: string = '';
  feedbackLvl: string = '';
  feedbackCommentsList: Feedback[] = [];
  lastInterviewLevel: string = '';
  interviewStatus: string = '';
  position: string = '';
  selectedButton: string = this.buttonSelection.Secondary;
  minDate: Date = new Date();
  interviewModeOptions = [
    { label: 'In Person', value: 'In Person' },
    { label: 'Virtual', value: 'Virtual' },
  ];
  existingPanelAvailability: { interviewDate?: string; interviewTime?: string; interviewMode?: string; updatedDate?: string; updatedBy?: string } | null = null;
  allowedPersonaTypes = new Set([
      this.personaType.OpsCandidates.toString(),
      this.personaType.vendorCandidates.toString(),
    ]);

  secondaryStatuses: any = [
  this.status.NoShow,
  this.status.OnHold,
  this.status.Others,
  this.status.ReschedulingRequested
];

  getLatestInterviewLevel(): string {
    if (!this.feedbackComments?.length) return '';
   const primaryLevels: any = [
      this.status.ProfileShortlisted,
      this.status.L1Schedule,
      this.status.L1Feedback,
      this.status.L2Schedule,
      this.status.L2Feedback,
      this.status.L3Schedule,
      this.status.L3Feedback,
      this.status.Hire,
      this.status.InProgess,
      this.status.Selected,
      this.status.ProfileReview,
      this.status.Joined,
      this.status.BackedOut,
      this.status.DroppedOut,
      this.status.L1Rejected,
      this.status.L2Rejected,
      this.status.L3Rejected,
      this.status.ProfileRejected

    ];

    for (let i = this.feedbackComments.length - 1; i >= 0; i--) {
      const feedbackTitle = this.feedbackComments[i]?.title;
      if (primaryLevels.includes(feedbackTitle)) {
        return feedbackTitle;
      }
    }
    return '';
  }

  constructor(
    private candidateFeedbackService: CandidateFeedbackService,
    private errorHandler: GlobalErrorService,
    private fb: FormBuilder
  ) {
    this.feedbackForm = this.fb.group({
      feedbackComment: ['', Validators.required],
      isButtonClicked: [false, this.buttonClickedValidator],
      interviewDate: [''],
      interviewTime: [''],
      interviewMode: [''],
    });
  }

  buttonClickedValidator(
    control: AbstractControl
  ): { [key: string]: any } | null {
    return control.value === true ? null : { buttonNotClicked: true };
  }

  timeValidator(control: AbstractControl): { [key: string]: any } | null {
    const value = control.value;
    if (!value) {
      return null; // Let required validator handle empty values
    }
    if (!this.validateTime(value)) {
      return { invalidTime: true };
    }
    return null;
  }

  ngOnInit() {}

  showButtonCondition() {
    const lowerCaseVisibility = this.fieldsVisibility?.toLowerCase() ?? '';
    const isOpsJobDet =
      lowerCaseVisibility === this.personaType.OpsTeamJobDet?.toLowerCase();
    const isOpsCandidates =
      lowerCaseVisibility === this.personaType.OpsCandidates?.toLowerCase();
    const isvendorCandidates =
      lowerCaseVisibility === this.personaType.vendorCandidates?.toLowerCase();
    const showButton = isOpsJobDet || isOpsCandidates || isvendorCandidates;
    return showButton;
  }

  showViewButton() {
    if (this.showButtonCondition()) {
      this.buttonLabel = ButtonLabel.View;
      this.buttonIcon = ButtonIcon.Visibility;
    } else {
      this.buttonLabel = ButtonLabel.Update;
      this.buttonIcon = ButtonIcon.Edit;
    }
    return true;
  }

  viewFunctionlityCondition(fieldsVisibility: string) {
    const lowerCaseVisibility = fieldsVisibility?.toLowerCase() ?? '';
    const isOpsTeamJobDet =
      lowerCaseVisibility === this.personaType.OpsTeamJobDet?.toLowerCase();
    const isvendorCandidates =
      lowerCaseVisibility === this.personaType.vendorCandidates?.toLowerCase();
    const isHireMgrCandidates =
      lowerCaseVisibility ===
      this.personaType.hiringMgrCandidates?.toLowerCase();
    const ishireMgrJobDet = this.personaType.HireMgrJobDet?.toLowerCase();
    const isViewable =
      isOpsTeamJobDet ||
      isvendorCandidates ||
      isHireMgrCandidates ||
      ishireMgrJobDet;
    return isViewable;
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case Status.ProfileReview.toLowerCase():
      case Status.ProfileShortlisted.toLowerCase():
        return StatusClass.Shortlist;
      case Status.ProfileRejected.toLowerCase():
      case Status.BackedOut.toLowerCase():
      case Status.DroppedOut.toLowerCase():
      case Status.L1Rejected.toLowerCase():
      case Status.L2Rejected.toLowerCase():
      case Status.L3Rejected.toLowerCase():
        return StatusClass.Dropped;
      case Status.Joined.toLowerCase():
      case Status.InProgess.toLowerCase():
        return StatusClass.Selected;
      default:
        return StatusClass.Pending;
    }
  }

  feedbackPopUp() {
    const popUp = new Set([
      this.personaType.hiringMgrCandidates.toString(),
      this.personaType.HireMgrJobDet.toString(),
      this.personaType.OpsCandidates.toString(),
      this.personaType.OpsTeamJobDet.toString(),
    ]);
    return popUp.has(this.fieldsVisibility);
  }

  getFeedback() {
    this.opened = true;
    this.candidateFeedbackService.getFeedbacks(this.candidateId).subscribe(
      (res: FeedbackResponse) => {
        this.candidateData = res?.data;
        this.position = res?.data?.position ;
        this.candidateName = res?.data?.candidateName;
        this.candidateStatus =
          res?.data?.status === this.status.Open
            ? this.status.ProfileReview
            : res?.data?.status;
        this.vendorName = res?.data?.vendorName;
        this.getFeedbackComments(res);
      },
      (error) => {
        this.errorHandler.logComponentError(error);
      }
    );
  }

  getFeedbackComments(res: FeedbackResponse) {
    const allFeedback = Array.isArray(res.data?.feedBack)
      ? res.data?.feedBack.map((feedback: Feedback) => {
          const feedbackItem: any = {
            title: feedback.interviewLevel?.interviewLevel?.toString() || feedback.interviewLevel?.toString() || '',
            content: feedback?.comments || '',
            status: feedback?.feedbackStatus || '',
            updatedBy: feedback?.updatedBy || '',
            updatedDate:
              feedback?.updatedDate != 'null'
                ? formatSingleDate((this.addISTOffset(feedback?.updatedDate)),yearDateFormat.feedbackDate)
                : '',
            open: false,
          };
          if (feedback?.interviewDate) {
            feedbackItem.interviewDate = feedback.interviewDate;
          }
          if (feedback?.interviewTime) {
            feedbackItem.interviewTime = feedback.interviewTime;
          }
          if (feedback?.interviewMode) {
            feedbackItem.interviewMode = feedback.interviewMode;
          }
          
          return feedbackItem;
        })
      : [];
    
    this.feedbackComments = allFeedback;
    this.scrollTimelineToBottom();

    if (Array.isArray(res.data?.feedBack)) {
      const allPanelAvailability = res.data.feedBack.filter(
        (feedback: Feedback) => {
          const levelValue = feedback.interviewLevel?.interviewLevel || feedback.interviewLevel?.toString() || '';
          return levelValue === this.status.PannelsAvailability;
        }
      );
      
      const panelAvailabilityFeedback = allPanelAvailability.length > 0 
        ? allPanelAvailability[allPanelAvailability.length - 1]
        : null;
      
      if (panelAvailabilityFeedback) {
        this.existingPanelAvailability = {
          interviewDate: panelAvailabilityFeedback.interviewDate,
          interviewTime: panelAvailabilityFeedback.interviewTime,
          interviewMode: panelAvailabilityFeedback.interviewMode,
          updatedDate: panelAvailabilityFeedback?.updatedDate != 'null' 
            ? formatSingleDate((this.addISTOffset(panelAvailabilityFeedback?.updatedDate)),yearDateFormat.feedbackDate)
            : '',
          updatedBy: panelAvailabilityFeedback.updatedBy
        };
      } else {
        this.existingPanelAvailability = null;
      }
    } else {
      this.existingPanelAvailability = null;
    }
  }

  private scrollTimelineToBottom() {
    // wait for the *ngFor timeline to render inside the just-opened modal
    setTimeout(() => {
      const el = this.timelineRef?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 50);
  }

  addISTOffset(date: string): string {
    const offsetMin = offsetMinutes
    const ms = Date.parse(date);
    const newDate = new Date(ms + offsetMin * 60 * 1000);
    const isoString = newDate.toISOString();
    return isoString;
  }

  handleHireButton() {
    const invalidStatuses = new Set([
      this.status.InProgess,
      this.status.BackedOut,
      this.status.DroppedOut,
      this.status.Joined,
      this.status.L1Rejected,
      this.status.L2Rejected,
      this.status.L3Rejected,
      this.status.ProfileShortlisted,
      this.status.ProfileReview,
      this.status.ProfileRejected,
      this.status.Hire,
      this.status.Selected,
    ].map(status => status.toString()));

    const latestLevel = this.getLatestInterviewLevel();
    const statusToCheck = latestLevel || this.candidateStatus;
    return !invalidStatuses.has(statusToCheck);
  }

  rejectButton() {
    const rejectStatuses = new Set([
      this.status.L1Schedule,
      this.status.L1Feedback,
      this.status.L2Schedule,
      this.status.L2Feedback,
      this.status.L3Schedule,
      this.status.L3Feedback,
      this.status.ProfileShortlisted,
    ].map(status => status.toString()));

    const latestLevel = this.getLatestInterviewLevel();
    return latestLevel === ""
      ? !rejectStatuses.has(this.candidateStatus)
      : rejectStatuses.has(latestLevel);
  }

  onboardingProgressButton(){
    const onboardingStatuses = new Set([
      this.status.Selected.toString(),
      this.status.Others.toString(),
      this.status.OnHold.toString(),
    ]);
    const latestLevel = this.getLatestInterviewLevel();
    return onboardingStatuses.has(latestLevel);
  }

  initiateOnboardingButton() {
    const selectStatuses = new Set([
      this.status.Hire.toString(),
      this.status.Others.toString(),
      this.status.OnHold.toString(),
    ]);
    const latestLevel = this.getLatestInterviewLevel();
    return selectStatuses.has(latestLevel);
  }

  backedOutButtonVisibility() {
    const buttonVisibility = new Set([
      this.status.Hire.toString(),
      this.status.InProgess.toString(),
      this.status.Selected.toString(),
    ]);
    const latestLevel = this.getLatestInterviewLevel();
    return buttonVisibility.has(latestLevel);
  }

  noShowButton(){
    const scheduleAndFeedbackLevels:any = [
      this.status.L1Schedule,
      this.status.L1Feedback,
      this.status.L2Schedule,
      this.status.L2Feedback,
      this.status.L3Schedule,
      this.status.L3Feedback,
    ];

    const excludeStatuses = new Set([
      this.status.L1Rejected,
      this.status.L2Rejected,
      this.status.L3Rejected,
      this.status.ProfileRejected,
      this.status.DroppedOut,
      this.status.BackedOut,
      this.status.Joined,
      this.status.InProgess,
      this.status.Selected,
      this.status.Hire,
      this.status.OnHold,
      this.status.Others,
    ].map(status => status.toString()));

    const latestLevel = this.getLatestInterviewLevel();

    if (this.allowedPersonaTypes.has(this.fieldsVisibility)) {
      if(latestLevel === this.status.L3Feedback && this.candidateStatus === this.status.Hire){
        return false;
      }
       return (
      (scheduleAndFeedbackLevels.includes(latestLevel) ||
        this.candidateStatus === this.status.NoShow ||
        this.candidateStatus === this.status.ReschedulingRequested) &&
      !excludeStatuses.has(latestLevel)
      );
    }
    return false;
  }

  reschedulingRequestedButton(){
    const scheduleAndFeedbackLevels: any = [
      this.status.L1Schedule,
      this.status.L1Feedback,
      this.status.L2Schedule,
      this.status.L2Feedback,
      this.status.L3Schedule,
      this.status.L3Feedback
    ];

    const excludeStatuses = new Set([
      this.status.L1Rejected,
      this.status.L2Rejected,
      this.status.L3Rejected,
      this.status.ProfileRejected,
      this.status.DroppedOut,
      this.status.BackedOut,
      this.status.Joined,
      this.status.InProgess,
      this.status.Selected,
      this.status.Hire,
    ].map(status => status.toString()));

    const latestLevel = this.getLatestInterviewLevel();
    if (this.allowedPersonaTypes.has(this.fieldsVisibility)) {
      if(latestLevel === this.status.L3Feedback && this.candidateStatus === this.status.Hire){
        return false;
      }
      return (
      (scheduleAndFeedbackLevels.includes(latestLevel) ||
        this.candidateStatus === this.status.NoShow ||
        this.candidateStatus === this.status.ReschedulingRequested) &&
      !excludeStatuses.has(latestLevel)
      );
    }
    return false;
  }

  shouldShowL1ScheduleButton(): boolean {
    const latestLevel = this.getLatestInterviewLevel();
    return latestLevel === this.status.ProfileShortlisted && this.isValidCandidateStatus();
  }

  shouldShowL1SelectedButton(): boolean {
    const latestLevel = this.getLatestInterviewLevel();
    return latestLevel === this.status.L1Schedule && this.isValidCandidateStatus();
  }

  shouldShowL2ScheduleButton(): boolean {
    const latestLevel = this.getLatestInterviewLevel();
    return latestLevel === this.status.L1Feedback && this.isValidCandidateStatus();
  }

  shouldShowL2SelectedButton(): boolean {
    const latestLevel = this.getLatestInterviewLevel();
    return latestLevel === this.status.L2Schedule && this.isValidCandidateStatus();
  }

  shouldShowL3ScheduleButton(): boolean {
    const latestLevel = this.getLatestInterviewLevel();
    return latestLevel === this.status.L2Feedback && this.isValidCandidateStatus();
  }

  shouldShowL3SelectedButton(): boolean {
    const latestLevel = this.getLatestInterviewLevel();
    return latestLevel === this.status.L3Schedule && this.isValidCandidateStatus();
  }

  isValidCandidateStatus(): boolean {
    const excludedStatuses = new Set([
      this.status.L1Rejected.toString(),
      this.status.L2Rejected.toString(),
      this.status.L3Rejected.toString(),
      this.status.ProfileRejected.toString(),
      this.status.DroppedOut.toString(),
      this.status.BackedOut.toString(),
      this.status.Joined.toString(),
    ]);
    return !excludedStatuses.has(this.candidateStatus);
  }

  onHoldButton(){
    const latestLevel = this.getLatestInterviewLevel();
    const excludeStatuses = new Set([
      this.status.L1Rejected.toString(),
      this.status.L2Rejected.toString(),
      this.status.L3Rejected.toString(),
      this.status.ProfileRejected.toString(),
      this.status.DroppedOut.toString(),
      this.status.BackedOut.toString(),
      this.status.Joined.toString(),
      this.status.ProfileReview.toString(),
    ]);
    if (this.allowedPersonaTypes.has(this.fieldsVisibility)) {
      return !excludeStatuses.has(latestLevel) && !excludeStatuses.has(this.candidateStatus);
    }
    return false;
  }

  othersButton(){
    const lastFeedbackTitle: any = this.feedbackComments?.at(-1)?.title ?? '';    
    const excludeStatuses = new Set([
      this.status.L1Rejected.toString(),
      this.status.L2Rejected.toString(),
      this.status.L3Rejected.toString(),
      this.status.ProfileRejected.toString(),
      this.status.DroppedOut.toString(),
      this.status.BackedOut.toString(),
      this.status.Joined.toString(),
      this.status.ProfileReview.toString(),
    ]);
    if(this.allowedPersonaTypes.has(this.fieldsVisibility)){
      const latest = this.getLatestInterviewLevel();
      return (!excludeStatuses.has(lastFeedbackTitle)) && !excludeStatuses.has(this.candidateStatus);
    }else{
      return false;
    }
  }

  joinedButton(){
    const joinStatuses = new Set([
      this.status.InProgess.toString(),
      this.status.Others.toString(),
      this.status.OnHold.toString(),
    ]);
    const latestLevel = this.getLatestInterviewLevel();
    return joinStatuses.has(latestLevel);
  }

  droppedOutButtonVisibility() {
    const opsOrHiringMgr = new Set([
      this.personaType.OpsCandidates.toString(),
      this.personaType.hiringMgrCandidates.toString(),
      this.personaType.HireMgrJobDet.toString(),
      this.personaType.OpsTeamJobDet.toString()
    ]);
    const vendorCandidates = this.personaType.vendorCandidates.toString();
    const rejectedStatuses = new Set([
      this.status.L1Rejected.toString(),
      this.status.L2Rejected.toString(),
      this.status.L3Rejected.toString(),
      this.status.ProfileRejected.toString(),
      this.status.DroppedOut.toString(),
      this.status.BackedOut.toString(),
      this.status.Joined.toString(),
      this.status.Hire.toString(),
      this.status.InProgess.toString(),
      this.status.Selected.toString(),
      this.status.Others.toString(),
      this.status.OnHold.toString(),
    ]);
    const latest = this.getLatestInterviewLevel();
    if (opsOrHiringMgr.has(this.fieldsVisibility)) {
      return !rejectedStatuses.has(latest);
    } else if (this.fieldsVisibility === vendorCandidates) {
      return  !rejectedStatuses.has(latest);
    } else {
      return false;
    }
  }

  rescheduleButtonVisibility() {
    if (this.allowedPersonaTypes.has(this.fieldsVisibility)) {
      return false;
    }
    const excludeStatuses = new Set([
      this.status.L1Rejected.toString(),
      this.status.L2Rejected.toString(),
      this.status.L3Rejected.toString(),
      this.status.ProfileRejected.toString(),
      this.status.Rejected.toString(),
      this.status.DroppedOut.toString(),
      this.status.Joined.toString(),
    ]);
    return !excludeStatuses.has(this.candidateStatus);
  }

  othersNewButtonVisibility() {
    if (this.allowedPersonaTypes.has(this.fieldsVisibility)) {
      return false;
    }
    const excludeStatuses = new Set([
      this.status.L1Rejected.toString(),
      this.status.L2Rejected.toString(),
      this.status.L3Rejected.toString(),
      this.status.ProfileRejected.toString(),
      this.status.Rejected.toString(),
      this.status.DroppedOut.toString(),
      this.status.Joined.toString(),
    ]);
    return !excludeStatuses.has(this.candidateStatus);
  }

  hidingForm() {
    const invalidStatuses = new Set([
      this.status.L1Rejected,
      this.status.L2Rejected,
      this.status.L3Rejected,
      this.status.Joined,
      this.status.BackedOut,
      this.status.DroppedOut,
      this.status.ProfileRejected,
    ].map(status => status.toString()));
    return !invalidStatuses.has(this.candidateStatus);
  }

  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    const day = ('0' + date.getDate()).slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = ('0' + date.getMinutes()).slice(-2);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strHours = ('0' + hours).slice(-2);
    return `${day}/${month}/${year} - ${strHours}:${minutes} ${ampm}`;
  }

  buttonClickCondition() {
    const lowerCaseVisibility = this.fieldsVisibility?.toLowerCase() ?? '';
    const isVendorCandidate =
      lowerCaseVisibility === this.personaType.vendorCandidates?.toLowerCase();
    const isHiringMgrCandidate =
      lowerCaseVisibility ===
      this.personaType.hiringMgrCandidates?.toLowerCase();
    const isHireMgrJobDet =
      lowerCaseVisibility === this.personaType.HireMgrJobDet?.toLowerCase();
    const shouldHandleClick =
      isVendorCandidate || isHiringMgrCandidate || isHireMgrJobDet;
    return shouldHandleClick;
  }

  onButtonClick() {
    this.feedbackForm.get('isButtonClicked')?.setValue(true);
    this.feedbackForm.get('isButtonClicked')?.updateValueAndValidity();
  }

  handleButtonClick(feedbackLvl: string, feedbackStatus: string) {
    this.onButtonClick();
    if (feedbackLvl === this.status.PannelsAvailability) {
      if (this.existingPanelAvailability) {
        if (this.existingPanelAvailability.interviewDate) {
          this.feedbackForm.patchValue({
            interviewDate: new Date(this.existingPanelAvailability.interviewDate)
          });
        }
        if (this.existingPanelAvailability.interviewTime) {
          const timeValue = this.existingPanelAvailability.interviewTime.substring(0, 5);
          this.feedbackForm.patchValue({
            interviewTime: timeValue
          });
        }
        if (this.existingPanelAvailability.interviewMode) {
          this.feedbackForm.patchValue({
            interviewMode: this.existingPanelAvailability.interviewMode
          });
        }
      }
      
      this.feedbackForm.get('interviewDate')?.setValidators([Validators.required]);
      this.feedbackForm.get('interviewTime')?.setValidators([Validators.required, this.timeValidator.bind(this)]);
      this.feedbackForm.get('interviewMode')?.setValidators([Validators.required]);
      this.feedbackForm.get('feedbackComment')?.clearValidators();
      this.feedbackForm.get('feedbackComment')?.setValue('');
    } else {
      this.feedbackForm.get('interviewDate')?.clearValidators();
      this.feedbackForm.get('interviewTime')?.clearValidators();
      this.feedbackForm.get('interviewMode')?.clearValidators();
      this.feedbackForm.get('interviewDate')?.setValue('');
      this.feedbackForm.get('interviewTime')?.setValue('');
      this.feedbackForm.get('interviewMode')?.setValue('');
      this.feedbackForm.get('feedbackComment')?.setValidators([Validators.required]);
    }
    this.feedbackForm.get('interviewDate')?.updateValueAndValidity();
    this.feedbackForm.get('interviewTime')?.updateValueAndValidity();
    this.feedbackForm.get('interviewMode')?.updateValueAndValidity();
    this.feedbackForm.get('feedbackComment')?.updateValueAndValidity();
    
    if (feedbackLvl === this.status.Rejected) {
     // const lastFeedbackTitle = this.feedbackComments?.at(-1)?.title ?? '';
      const lastFeedbackTitle: any = this.getLatestInterviewLevel();;
      const statusMap: { [key: string]: string } = {
        [this.status.L1Schedule]: this.status.L1Rejected,
        [this.status.L1Feedback]: this.status.L1Rejected,
        [this.status.L2Schedule]: this.status.L2Rejected,
        [this.status.L2Feedback]: this.status.L2Rejected,
        [this.status.L3Schedule]: this.status.L3Rejected,
        [this.status.L3Feedback]: this.status.L3Rejected,
      };
      this.feedbackStatus =
        statusMap[lastFeedbackTitle] || this.status.ProfileRejected;
      this.feedbackLvl = this.feedbackStatus;
    } else {
      this.feedbackStatus = feedbackStatus;
      this.feedbackLvl = feedbackLvl;
    }
  }

  submitFeedback() {
    if (this.feedbackForm.invalid) {
      this.feedbackForm.markAllAsTouched();
      this.opened = true;
    } else {
      const feedbackComment = this.feedbackForm.get('feedbackComment')?.value;
      this.updateFeedbackReq(feedbackComment);
    }
  }
  updateFeedbackReq(feedbackComment: string) {
    const updatedBy = JSON.parse(
      sessionStorage.getItem('userProfile') as string
    );
    
    const feedbackData: UpdateFeedbackRequest = {
      candidateId: this.candidateData?.candidateId ?? 0,
      candidateName: this.candidateData?.candidateName ?? '',
      emailId: this.candidateData?.emailId ?? '',
      hiringRequestId: this.candidateData?.hiringRequestId ?? '',
      position: this.candidateData?.position ?? '',
      status: this.candidateData?.status ?? '',
      vendorName: this.candidateData?.vendorName ?? '',
      interviewLevel: this.feedbackLvl ?? '',
      feedbackStatus: this.feedbackStatus ?? '',
      comments: feedbackComment || null,
      updatedBy: `${updatedBy.firstName} ${updatedBy.lastName}`
    };
    
    if (this.feedbackLvl === this.status.PannelsAvailability) {
      const dateValue = this.feedbackForm.get('interviewDate')?.value;
      const timeValue = this.feedbackForm.get('interviewTime')?.value;
      
      if (dateValue) {
        const date = new Date(dateValue);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        feedbackData.interviewDate = `${year}-${month}-${day}`;
      }
      
      if (timeValue) {
        const timeParts = timeValue.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
        if (timeParts) {
          let hours = parseInt(timeParts[1]);
          const minutes = timeParts[2];
          const meridiem = timeParts[3]?.toUpperCase();
          if (meridiem === 'PM' && hours !== 12) {
            hours += 12;
          } else if (meridiem === 'AM' && hours === 12) {
            hours = 0;
          }
          
          feedbackData.interviewTime = `${String(hours).padStart(2, '0')}:${minutes}:00`;
        } else {
          feedbackData.interviewTime = timeValue.includes(':') ? `${timeValue}:00` : timeValue;
        }
      }
      const modeValue = this.feedbackForm.get('interviewMode')?.value;
      if (modeValue) {
        feedbackData.interviewMode = typeof modeValue === 'object' ? modeValue.value : modeValue;
      }
      feedbackData.comments = null;
    }
    
    this.candidateFeedbackService.updateFeedback(feedbackData).subscribe(
      () => {
        if (feedbackData.feedbackStatus) {
          this.sendFeedbackStatus.emit(feedbackData.feedbackStatus);
        }
        this.feedbackForm.reset();
        this.feedbackLvl = '';
        this.selectedButton = this.buttonSelection.Secondary;
        this.opened = false;
      },
      (error) => {
        this.errorHandler.logComponentError(error);
        this.opened = false;
      }
    );
  }

  cancel() {
    // Always reset form on cancel
    this.feedbackForm.reset();
    this.feedbackForm.get('isButtonClicked')?.setValue(false);
    this.feedbackForm.get('isButtonClicked')?.updateValueAndValidity();
    this.feedbackLvl = '';
    this.selectedButton = this.buttonSelection.Secondary;
  }

  formatTimeInput(event: any): void {
    let value = event.target.value;
    value = value.replace(/[^\d:]/g, '');
    
    if (!value.includes(':') && value.length >= 3) {
      value = value.substring(0, 2) + ':' + value.substring(2, 4);
    } else if (value.includes(':')) {
      const parts = value.split(':');
      if (parts.length === 2) {
        const hours = parts[0].padStart(2, '0').substring(0, 2);
        const minutes = parts[1].substring(0, 2);
        value = `${hours}:${minutes}`;
      }
    }
    
    if (value.length > 5) {
      value = value.substring(0, 5);
    }
    
    this.feedbackForm.get('interviewTime')?.setValue(value, { emitEvent: false });
    
    if (value.length === 5 && value.includes(':')) {
      const isValid = this.validateTime(value);
      if (!isValid) {
        this.feedbackForm.get('interviewTime')?.setErrors({ invalidTime: true });
      } else {
        const currentErrors = this.feedbackForm.get('interviewTime')?.errors;
        if (currentErrors && currentErrors['invalidTime']) {
          delete currentErrors['invalidTime'];
          const remainingErrors = Object.keys(currentErrors).length > 0 ? currentErrors : null;
          this.feedbackForm.get('interviewTime')?.setErrors(remainingErrors);
        }
      }
    }
  }

  validateTime(time: string): boolean {
    if (!time || time.length !== 5) {
      return false;
    }
    const timeRegex = /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/;
    if (!timeRegex.test(time)) {
      return false;
    }
    const [hours, minutes] = time.split(':').map(Number);
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
  }

  pannelAvailability() {
    const opsPersonaTypes = new Set([
      this.personaType.OpsCandidates.toString(),
      this.personaType.OpsTeamJobDet.toString(),
    ]);
    
    if (!opsPersonaTypes.has(this.fieldsVisibility)) {
      return false;
    }
    
    const validStatuses = new Set([
      this.status.L1Pending.toString(),
      this.status.L2Pending.toString(),
      this.status.L3Pending.toString(),
      this.status.ProfileShortlisted.toString(),
      this.status.ReschedulingRequested.toString(),
    ]);
    return validStatuses.has(this.candidateStatus);
  }

  getPanelAvailabilityMetadata(): { updatedDate?: string; updatedBy?: string } | null {
    return this.existingPanelAvailability ? {
      updatedDate: this.existingPanelAvailability.updatedDate,
      updatedBy: this.existingPanelAvailability.updatedBy
    } : null;
  }


}
